import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { OPERATOR_ERROR, isRetryable } from '../operator/operatorErrors';
import { earlierVisitFailure, offScreenFailure, offScreenSignal } from '../operator/operatorTaskScope';
import { closeReceipt, countReceipt, findReceipt, scanReceivingCode } from './operatorReceivingThunks';
import { closeMessage, countMessage, excessMessage, shortageMessage } from './operatorReceivingUtils';

/**
 * What the band says for each receiving outcome; see `operatorListeners`.
 * A write can answer once its receipt is off screen (`meta.offScreenTask`):
 * the band then names the receipt and says no step to take. A lost answer
 * offers «Reintentar» wherever it shows: the band carries the write.
 */
export const RECEIVING_SIGNAL_RULES = {
  [scanReceivingCode.typePrefix]: {
    success: ({ payload }) => {
      if (payload.kind === 'slot') {
        return { tone: 'step', message: `Ubicación ${payload.slot.name || payload.slot.code}. Escanea un producto.` };
      }
      if (payload.kind === 'line') {
        const { sku, name, factor } = payload.draft;
        return { tone: 'step', message: `${sku} — ${name}${factor > 1 ? ` · caja ×${factor}` : ''}. ¿Cuántos?` };
      }
      return { tone: 'warning', message: 'Ese código es de más de una línea: elige cuál.' };
    },
  },
  // The receipt it finds opens, and its screen speaks. Once the queue is off screen, nothing: a read did nothing.
  [findReceipt.typePrefix]: {
    success: () => null,
    error: ({ meta }) => (meta.offScreenTask ? null : undefined),
  },
  [countReceipt.typePrefix]: {
    success: ({ payload, meta }) => {
      const signal = countMessage(payload, meta.arg);
      return meta.offScreenTask ? offScreenSignal(meta.offScreenTask, signal) : signal;
    },
    // Over what a line lacks nothing was registered: no «Reintentar», the sheet asks.
    error: ({ payload, meta }, fallback) => {
      const name = meta.offScreenTask;
      const over = payload?.kind === OPERATOR_ERROR.CONFIRM && payload.excess;
      if (meta.earlierVisit) return earlierVisitFailure(payload);
      if (!name) {
        return over ? { tone: 'warning', message: `${excessMessage(payload.excess)} Confírmalo o corrige la cantidad.` } : undefined;
      }
      if (isRetryable(payload)) return offScreenSignal(name, fallback);
      if (over) return offScreenSignal(name, { tone: 'warning', message: `no se registró. ${excessMessage(payload.excess)}` });
      return offScreenFailure(name, payload);
    },
    retry: OPERATOR_FLOW.RECEIVING,
    keyed: true,
  },
  [closeReceipt.typePrefix]: {
    // «REC-000090 cerrada» names the receipt already.
    success: ({ payload }) => ({ tone: 'success', message: closeMessage(payload) }),
    error: ({ payload, meta }, fallback) => {
      const name = meta.offScreenTask;
      const short = payload?.kind === OPERATOR_ERROR.CONFIRM;
      if (meta.earlierVisit) return earlierVisitFailure(payload);
      if (!name) {
        return short ? { tone: 'warning', message: `${shortageMessage(payload.missing)} Confírmalo para cerrar.` } : undefined;
      }
      if (isRetryable(payload)) return offScreenSignal(name, fallback);
      if (short) return offScreenSignal(name, { tone: 'warning', message: `no se cerró. ${shortageMessage(payload.missing)}` });
      return offScreenFailure(name, payload, 'no se cerró');
    },
    retry: OPERATOR_FLOW.RECEIVING,
    keyed: true,
  },
};
